import React from 'react';

import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

import { GREEN, RED } from './constants';
import { theme as baseThemeObject } from './ThemeObject';

import type { ReactNode } from 'react';

// Raised from MUI's defaults, not the inherited theme's, so a ThemeContext nested inside another
// (shell pages inside the shell's theme) keeps modals below the dialogs pinned at zIndex 20000.
const MUI_Z_INDEX = createTheme().zIndex;

interface ThemeContextProps {
  children: ReactNode;
}

const ThemeContext = ({ children }: ThemeContextProps) => {
  const baseTheme = useTheme();

  const theme = createTheme({
    // `palette` must stay nested. Spreading it here (as this did until
    // 2026-07) puts `primary`/`secondary`/… at the root of the options
    // object, where MUI never reads them — so `theme.palette.primary.main`
    // silently resolved to MUI's default #1976d2 instead of SCOUT_BLUE.
    // `baseThemeObject.props` and `.overrides` are deliberately NOT passed:
    // they are MUI v4 keys, dead since the v5 upgrade, and the current UI is
    // designed against their absence. See ThemeObject.ts.
    palette: baseThemeObject.palette,
    typography: {
      ...baseThemeObject.typography,
      button: {
        textTransform: 'none',
        fontWeight: baseTheme.typography.fontWeightBold
      }
    },
    zIndex: {
      modal: MUI_Z_INDEX.modal + 10000,
      snackbar: MUI_Z_INDEX.snackbar + 10000,
      tooltip: MUI_Z_INDEX.tooltip + 10000
    },
    components: {
      MuiListItemIcon: {
        styleOverrides: { root: { minWidth: 56 } }
      },
      // MuiTab inherits typography.button, whose Scout weight is bold for real
      // buttons. Keep tabs at MUI's medium default; individual tab groups can
      // still make the selected tab semibold through their local styles.
      MuiTab: {
        styleOverrides: {
          root: {
            fontWeight: baseTheme.typography.fontWeightMedium
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: '14px',
            fontFamily: 'Source Sans Pro, sans-serif'
          }
        }
      },
      // MUI derives a standard Alert's text AND background from
      // `palette[severity].light`, treating it as a mid-tone it can darken and
      // lighten. Scout's `*.light` tokens are near-white surfaces
      // (error.light #FCEFEF), so the derivation collapses to a white
      // background with grey text and the alert stops reading as an alert.
      // Pin the three severities Scout has tokens for to the same pairs
      // ComponentsLibrary/Snackbar uses, so the two agree. `info` is left to
      // MUI — Scout has no info token and MUI's default light blue derives fine.
      MuiAlert: {
        styleOverrides: {
          root: {
            variants: [
              {
                props: { variant: 'standard', severity: 'error' },
                style: { backgroundColor: '#FCF2F2', color: RED }
              },
              {
                props: { variant: 'standard', severity: 'success' },
                style: { backgroundColor: '#EFFDF3', color: GREEN }
              },
              {
                props: { variant: 'standard', severity: 'warning' },
                style: { backgroundColor: '#FDF6EE', color: '#C9781D' }
              }
            ]
          }
        }
      }
    }
  });

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default ThemeContext;

ThemeContext.propTypes = {
  children: PropTypes.node
};

type WithScoutThemeProps = object;

const withScoutTheme = <T extends WithScoutThemeProps>(Component: React.ComponentType<T>) => {
  const WrapperComponent = (props: T) => (
    <ThemeContext>
      <Component {...props} />
    </ThemeContext>
  );
  return WrapperComponent;
};

export { withScoutTheme };
